// Cost Dashboard — shared formatters, date math, and the AI-draft prompt
// template. Everything here is pure / data-only; no React, no Supabase.

// Compact currency: $1.2k / $847 / $0.42. For compact tile/row figures.
export function fmtCompact(n) {
  const v = Number(n);
  if (!isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1000) return `${v < 0 ? '−' : ''}$${(abs / 1000).toFixed(1)}k`;
  if (abs >= 100)  return `${v < 0 ? '−' : ''}$${Math.round(abs)}`;
  return `${v < 0 ? '−' : ''}$${abs.toFixed(2)}`;
}

// Full currency: $1,234.56. For drilldown / detail rows where the operator
// is reading the exact figure.
export function fmtFull(n) {
  const v = Number(n);
  if (!isFinite(v)) return '—';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Sign-aware percent. Returns "+12.5%" / "−3.1%" / "0.0%". Caller picks color.
export function fmtSignedPct(n) {
  const v = Number(n);
  if (!isFinite(v)) return '—';
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  return `${sign}${Math.abs(v).toFixed(1)}%`;
}

// "3 days ago" / "2 weeks ago" / "Apr 12". Falls back to short date past 60d.
export function fmtRelativeDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  const now = new Date();
  const days = Math.round((now - d) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const w = Math.round(days / 7);
    return `${w} week${w === 1 ? '' : 's'} ago`;
  }
  if (days < 60) return '1 month ago';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Compute (start, end) ISO dates for a time-range key. Bounds today on the
// upper end (no future-dated invoices). Custom is passed-through.
export function computeRangeDates(rangeKey, customStart, customEnd) {
  const today = new Date();
  const end = today.toISOString().split('T')[0];
  if (rangeKey === 'custom' && customStart && customEnd) {
    return { start: customStart, end: customEnd };
  }
  const days = rangeKey === '7d' ? 7 : rangeKey === '90d' ? 90 : 30;
  const start = new Date(today);
  start.setDate(today.getDate() - (days - 1));
  return { start: start.toISOString().split('T')[0], end };
}

// Sign-aware text color token for delta values. Up = critical (cost rising
// is bad for the operator). Down = success (cost falling is good).
export function deltaColor(deltaPct, deltaClass) {
  if (deltaClass === 'pack_changed') return 'var(--text-warning)';
  if (deltaClass === 'new_item')     return 'var(--text-info)';
  const v = Number(deltaPct);
  if (!isFinite(v) || Math.abs(v) < 0.5) return 'var(--text-muted)';
  return v > 0 ? 'var(--text-critical)' : 'var(--text-success)';
}

// Sign-aware StatusPill tone. Maps the same way as deltaColor.
export function deltaPillTone(deltaPct, deltaClass) {
  if (deltaClass === 'pack_changed') return 'amber';
  if (deltaClass === 'new_item')     return 'blue';
  const v = Number(deltaPct);
  if (!isFinite(v) || Math.abs(v) < 0.5) return 'neutral';
  return v > 0 ? 'red' : 'green';
}

// Annualized dollar impact estimate (for outreach copy + UI tooltips).
// Assumes the in-window volume scales linearly to a year — a real-world
// approximation good enough for negotiation framing.
export function annualizeImpact(dollarImpact, windowStart, windowEnd) {
  if (!isFinite(Number(dollarImpact))) return null;
  const s = new Date(windowStart + 'T00:00:00');
  const e = new Date(windowEnd + 'T00:00:00');
  const days = Math.max(1, Math.round((e - s) / 86400000) + 1);
  return Number(dollarImpact) * (365 / days);
}

// ── AI draft generation ────────────────────────────────────────────────────
//
// System prompt for distributor-outreach drafts. Hard rule: NEVER invent
// facts. Operator-facing emails about money are a place where hallucination
// has real cost — wrong number → embarrassing email to distributor. The
// model is instructed to use ONLY the data block, and to skip a claim
// rather than fabricate one when fields are missing.
export const DRAFT_SYSTEM_PROMPT = `You are helping a restaurant operator negotiate with their food distributor. Generate a brief, professional email (3-5 sentences) that references specific recent price data and asks if the price can be revisited. The tone is firm but cordial — operators have ongoing relationships with their distributors and don't want to burn bridges. Do NOT make threats. Do NOT promise to switch vendors. Just present the data and ask the question.

HARD RULES — never invent facts:
- Use ONLY figures from the structured DATA block. Do not estimate, round generously, or extrapolate.
- If a field is missing from the data block, do not reference it. Skip the claim entirely rather than guess.
- Do not claim a "pattern" of consecutive increases unless consecutive_increases is provided and ≥ 3.
- Do not invent a vendor email or contact name. If primary_contact is missing, sign off with operator first name only.
- Do not say "we're considering switching vendors" or imply any threat.

OUTPUT FORMAT — return JSON only, no preamble:
{"subject": "...", "body": "..."}

The subject should be specific (e.g. "Question on recent {item} pricing"). The body should reference the change politely, ask for a revisit, and end with the operator's first name and restaurant name.`;

// Build the user-message data block for one item. All fields optional —
// the prompt is instructed to skip missing ones rather than invent.
export function buildDraftDataBlock({
  itemName,
  vendorName,
  baselinePrice,
  currentPrice,
  baselineDate,
  currentDate,
  unit,
  deltaPct,
  weeksElapsed,
  volume,
  volumeUnit,
  consecutiveIncreases,
  annualImpact,
  operatorFirstName,
  restaurantName,
}) {
  const lines = ['DATA:'];
  if (itemName)             lines.push(`- Item: ${itemName}`);
  if (vendorName)           lines.push(`- Distributor: ${vendorName}`);
  if (baselinePrice != null && unit)
    lines.push(`- Baseline price: $${Number(baselinePrice).toFixed(2)} per ${unit}${baselineDate ? ` (invoice from ${baselineDate})` : ''}`);
  if (currentPrice != null && unit)
    lines.push(`- Current price: $${Number(currentPrice).toFixed(2)} per ${unit}${currentDate ? ` (invoice from ${currentDate})` : ''}`);
  if (deltaPct != null && weeksElapsed != null)
    lines.push(`- Change: ${Number(deltaPct).toFixed(1)}% over ${weeksElapsed} weeks`);
  else if (deltaPct != null)
    lines.push(`- Change: ${Number(deltaPct).toFixed(1)}%`);
  if (volume != null && volumeUnit)
    lines.push(`- Volume: ${Number(volume).toFixed(0)} ${volumeUnit} purchased in this window`);
  if (consecutiveIncreases != null && consecutiveIncreases >= 3)
    lines.push(`- Consecutive increases observed: ${consecutiveIncreases}`);
  if (annualImpact != null)
    lines.push(`- Annual impact at current volume: $${Math.round(Number(annualImpact)).toLocaleString('en-US')}`);
  if (operatorFirstName)
    lines.push(`- Operator first name: ${operatorFirstName}`);
  if (restaurantName)
    lines.push(`- Restaurant name: ${restaurantName}`);
  return lines.join('\n');
}

// Static fallback used when AI generation fails. Never blocks the section.
export function buildFallbackDraft({ itemName, vendorName, deltaPct, operatorFirstName, restaurantName }) {
  const subject = itemName ? `Question on recent ${itemName} pricing` : 'Question on recent pricing';
  const deltaText = deltaPct != null ? `moved ${Number(deltaPct).toFixed(1)}% recently` : 'moved recently';
  const sign = operatorFirstName || (restaurantName ? `— ${restaurantName}` : '');
  const body = [
    `Hi${vendorName ? ` ${vendorName} team` : ''},`,
    '',
    `I noticed our pricing on ${itemName || 'a recent order'} has ${deltaText}. Could we discuss whether there's room to revisit?`,
    '',
    'Thanks,',
    [operatorFirstName, restaurantName].filter(Boolean).join(' · ') || sign,
  ].filter(Boolean).join('\n');
  return { subject, body };
}
